import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import MailSvg from "@/src/assets/icons/icon-mail.svg";
import CopySvg from "@/src/assets/icons/icon-copy-black.svg";
import BasicButton from "@/src/components/atom/Button/BasicButton";
import Checkbox from "@/src/components/atom/Checkbox";
import Typo from "@/src/components/atom/Typo";
import Icon from "@/src/components/atom/Icon";
import PublicPageHeader from "@/src/components/organism/PublicPageHeader";
import PublicPageFooter from "@/src/components/molecule/PublicPageFooter";
import SendLinkCodeDialog from "@/src/components/organism/SendLinkCodeDialog";
import useAlert from "@/src/hooks/useAlert";
import {
  useSendCompanyCodeMutation,
  useUpdateExporterDomainSettingsMutation,
} from "@/src/store/apis/corporate/company";
import { useGetCurrentSubscriptionQuery } from "@/src/store/apis/subscription";
import { useAppSelector } from "@/src/store";
import PUBLIC_PATH from "@/src/routes/public/path";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import dayjs from "dayjs";

interface LocationState {
  corpCode: string;
  companyName?: string;
}

const WorkspaceSuccess: React.FC = () => {
  const { t } = useTranslation(["signup", "common", "error", "alert"]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const alert = useAlert();
  const [sendCompanyCode, { isLoading: isSendingEmail }] =
    useSendCompanyCodeMutation();
  const [updateExporterDomainSettings, { isLoading: isUpdatingSettings }] =
    useUpdateExporterDomainSettingsMutation();

  const { data: currentSubscription } = useGetCurrentSubscriptionQuery();

  const locationState = location.state as LocationState | null;
  const corpCode = locationState?.corpCode || "";

  const subscriptionInfo = useMemo(() => {
    if (!currentSubscription) return null;

    const startDate =
      currentSubscription.localStartDate ||
      currentSubscription.subscriptionStartAtFrom;
    const endDate =
      currentSubscription.localEndDate ||
      currentSubscription.subscriptionEndAtTo;

    const freeDays =
      currentSubscription.freeCodeList?.[0]?.freeDateCount ||
      (startDate && endDate ? dayjs(endDate).diff(dayjs(startDate), "day") : 0);

    return {
      planName: currentSubscription.plan?.name || "",
      startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
      freeDays,
    };
  }, [currentSubscription]);

  const allowedEmailDomain = useMemo(() => {
    if (user && "accountDto" in user) {
      return user.accountDto?.email?.split("@")[1] || "";
    }
    if (user && "account" in user) {
      return user.account?.email?.split("@")[1] || "";
    }
    return "";
  }, [user]);

  const [isAutoDomainRegistration, setIsAutoDomainRegistration] =
    useState(true);
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);

  React.useEffect(() => {
    if (!corpCode) {
      navigate(PUBLIC_PATH.SIGNUP_WORKSPACE, { replace: true });
    }
  }, [corpCode, navigate]);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(corpCode);
      alert.showAlert({ type: "success", message: t("common:copied") });
    } catch (error) {
      console.error("Failed to copy:", error);
      alert.showAlert({ type: "error", message: t("error:copyFailed") });
    }
  };

  const handleSendEmail = () => {
    setSendEmailDialogOpen(true);
  };

  const handleSendEmailSubmit = async (emailList: string[]) => {
    try {
      await sendCompanyCode({ emailList }).unwrap();

      setSendEmailDialogOpen(false);
      alert.showAlert({
        type: "success",
        message: t("alert:sendSuccess"),
      });
    } catch (error: any) {
      const message = Array.isArray(error.data?.message)
        ? error.data.message[0]
        : error.data?.message || t("common:somethingWentWrong");
      alert.showAlert({ type: "error", message });
    }
  };

  const handleAutoDomainToggle = async (checked: boolean) => {
    try {
      await updateExporterDomainSettings({
        isAutoDomainRegistration: checked,
      }).unwrap();

      setIsAutoDomainRegistration(checked);
    } catch (error: any) {
      console.error("Failed to update domain settings:", error);
      const message = Array.isArray(error.data?.message)
        ? error.data.message[0]
        : error.data?.message || t("common:somethingWentWrong");

      alert.showAlert({ type: "error", message });
    }
  };

  const handleGoToDashboard = async () => {
    const destination = user?.exporter?.isActivated
      ? EXPORTER_PRIVATE_PATH.HOME
      : PUBLIC_PATH.LOGIN;

    navigate(destination, { replace: true });
  };

  if (!corpCode) {
    return null;
  }

  return (
    <Container>
      <PublicPageHeader />

      <ContentWrapper>
        <TitleSection>
          <MainTitle>
            <Typo typoType="d3">{t("signup:v2.workspaceCreated")}</Typo>
          </MainTitle>
          <SubTitle>
            <Typo typoType="b6r" color="gray4">
              {t("signup:v2.workspaceSubDesc")}
            </Typo>
          </SubTitle>
        </TitleSection>

        {subscriptionInfo && (
          <FreeTrialCard>
            <FreeTrialTitle>{subscriptionInfo.planName}</FreeTrialTitle>
            <FreeTrialDateSection>
              <FreeTrialDate>
                {subscriptionInfo.startDate} ~ {subscriptionInfo.endDate}
              </FreeTrialDate>
              <FreeTrialBadge>
                {t("signup:v2.freeTrialDays", {
                  days: subscriptionInfo.freeDays,
                })}
              </FreeTrialBadge>
            </FreeTrialDateSection>
            <FreeTrialNote>
              <CheckIcon>✓</CheckIcon>
              <FreeTrialNoteText>
                {t("signup:v2.freeTrialNote")}
              </FreeTrialNoteText>
            </FreeTrialNote>
          </FreeTrialCard>
        )}

        <SuccessCard>
          <MailIconWrapper>
            <Icon iconSrc={MailSvg} iconSize={56} />
          </MailIconWrapper>

          <TextContent>
            <CardTitle>
              <Typo typoType="h2">{t("signup:v2.inviteTeamMembers")}</Typo>
            </CardTitle>
            <CardDescription>
              <Typo typoType="b7m" color="gray1">
                {t("signup:v2.inviteDescription")}
              </Typo>
            </CardDescription>
          </TextContent>

          <FormSection>
            <FormLabel>
              <Typo typoType="b7m" color="gray2">
                {t("signup:v2.corpCode")}
              </Typo>
            </FormLabel>

            <CodeDisplayBox>
              <CodeText>
                <Typo typoType="d5" color="blue4">
                  {corpCode}
                </Typo>
              </CodeText>
              <CopyButton onClick={handleCopyCode}>
                <Icon iconSrc={CopySvg} iconSize={20} />
              </CopyButton>
            </CodeDisplayBox>

            <CheckboxWrapper>
              <CheckboxContainer>
                <Checkbox
                  id="auto-domain-registration"
                  checked={isAutoDomainRegistration}
                  onChange={(e) => handleAutoDomainToggle(e.target.checked)}
                  disabled={isUpdatingSettings}
                />
                <CheckboxLabel htmlFor="auto-domain-registration">
                  <Typo typoType="b9m" color="gray2">
                    {t("signup:v2.autoDomainRegistration", {
                      allowedEmailDomain,
                    })}
                  </Typo>
                </CheckboxLabel>
              </CheckboxContainer>
            </CheckboxWrapper>
          </FormSection>

          <ButtonGroup>
            <BasicButton
              buttonGrade="primary"
              buttonColor="blue"
              buttonSize={40}
              onClick={handleSendEmail}
              style={{ width: "100%" }}
            >
              {t("signup:v2.sendCorpCodeEmail")}
            </BasicButton>
            <BasicButton
              buttonGrade="secondary"
              buttonColor="blue"
              buttonSize={40}
              onClick={handleGoToDashboard}
              style={{ width: "100%" }}
            >
              {t("signup:v2.goToDashboard")}
            </BasicButton>
          </ButtonGroup>
        </SuccessCard>
      </ContentWrapper>

      <PublicPageFooter />

      <SendLinkCodeDialog
        open={sendEmailDialogOpen}
        onOpenChange={setSendEmailDialogOpen}
        linkCode={corpCode}
        onOk={handleSendEmailSubmit}
        isLoading={isSendingEmail}
        title={t("common:sendCorporateCode")}
        description={`${t("signup:v2.workspace.modal.sendCorporateCodeGuide1")}\n${t("signup:v2.workspace.modal.sendCorporateCodeGuide2")}`}
        codeLabel={t("common:corporateCode")}
      />
    </Container>
  );
};

export default WorkspaceSuccess;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${colorSet.white};
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 60px 40px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const MainTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 24px 0;

  ${typo.d3}
  color: ${colorSet.gray1};
  text-align: center;
`;

const SubTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  ${typo.b7m}
  color: ${colorSet.gray1};
  text-align: center;
`;

const FreeTrialCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 720px;
  padding: 24px 48px;
  background: ${colorSet.gray11};
  border: 1px solid ${colorSet.gray10};
  border-radius: 16px;
`;

const FreeTrialTitle = styled.div`
  ${typo.h4}
  color: ${colorSet.gray2};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FreeTrialDateSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const FreeTrialDate = styled.div`
  ${typo.b7m}
  color: ${colorSet.gray2};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FreeTrialBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  background: ${colorSet.systemBlue6};
  border-radius: 24px;
  ${typo.b11m}
  color: ${colorSet.systemBlue2};
`;

const FreeTrialNote = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 18px;
`;

const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${colorSet.blue4};
  font-size: 12px;
  font-weight: bold;
`;

const FreeTrialNoteText = styled.p`
  ${typo.b9r}
  color: ${colorSet.gray1};
  white-space: nowrap;
`;

const SuccessCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 720px;
  padding: 40px 48px;
  background: ${colorSet.white};
  border-radius: 16px;
  box-shadow:
    0px 0px 0px 1px rgba(0, 0, 0, 0.1),
    0px 4px 16px 0px rgba(0, 0, 0, 0.1);
`;

const MailIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: center;
`;

const CardTitle = styled.div`
  ${typo.h2}
  width: 100%;
`;

const CardDescription = styled.div`
  ${typo.b7m}
  color: ${colorSet.gray1};
  width: 100%;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const FormLabel = styled.div`
  ${typo.b7m}
  color: ${colorSet.gray2};
`;

const CodeDisplayBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 62px;
  padding: 12px 16px;
  background: ${colorSet.white};
  border: 1px solid ${colorSet.gray10};
  border-radius: 8px;
`;

const CodeText = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  ${typo.d5}
  color: ${colorSet.blue4};
`;

const CopyButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 6px;
  background: ${colorSet.white};
  border-radius: 8px;
  box-shadow:
    0px 0px 0px 0.5px rgba(160, 164, 171, 0.2),
    0px 1px 2px 0px rgba(5, 29, 57, 0.1);

  &:hover {
    box-shadow:
      0px 0px 0px 0.5px rgba(160, 164, 171, 0.2),
      0px 2px 4px 0px rgba(5, 29, 57, 0.2);
    transform: translateY(-1px);
  }

  &:active {
    box-shadow:
      0px 0px 0px 1px rgba(5, 29, 57, 0.6),
      0px 0px 0px 2px rgba(5, 29, 57, 0.2),
      0px 2px 4px 0px rgba(5, 29, 57, 0.2);
  }

  transition: all 0.15s;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;
